import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { analyticsService } from '../../services/analyticsService';

const { 
  FiTrendingUp, FiUsers, FiCalendar, FiDollarSign, FiDownload, FiRefreshCw, 
  FiBarChart3, FiPieChart, FiClock, FiMapPin, FiActivity, FiAlertCircle,
  FiCheckCircle, FiTarget, FiUserCheck, FiSettings, FiWifi, FiWifiOff,
  FiMaximize2, FiMinimize2, FiGrid, FiList, FiFilter
} = FiIcons;

const AdvancedAnalytics = () => {
  const [overallMetrics, setOverallMetrics] = useState({});
  const [demographics, setDemographics] = useState({});
  const [appointmentAnalytics, setAppointmentAnalytics] = useState({});
  const [revenueAnalytics, setRevenueAnalytics] = useState({});
  const [timeAnalytics, setTimeAnalytics] = useState({});
  const [bookingsByDate, setBookingsByDate] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('checking');
  const [viewMode, setViewMode] = useState('detailed'); // detailed, compact
  const [expandedCard, setExpandedCard] = useState(null);

  // Format currency in INR
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // Format percentage
  const formatPercentage = (value) => {
    return `${parseFloat(value || 0).toFixed(1)}%`;
  };

  // Load all analytics data
  const loadAnalyticsData = async () => {
    setLoading(true);
    setError('');
    setConnectionStatus('checking');

    try {
      // Test connection first
      const connectionTest = await analyticsService.testConnection();
      if (!connectionTest.success) {
        setConnectionStatus('failed');
        setError('Cannot connect to analytics database: ' + (connectionTest.error?.message || 'Unknown error'));
        setLoading(false);
        return;
      }

      setConnectionStatus('connected');

      // Load all analytics data in parallel
      const [
        metricsResult,
        demographicsResult,
        appointmentResult,
        revenueResult,
        timeResult,
        dateRangeResult
      ] = await Promise.all([
        analyticsService.getOverallMetrics(),
        analyticsService.getPatientDemographics(),
        analyticsService.getAppointmentTypeAnalytics(),
        analyticsService.getRevenueAnalytics(),
        analyticsService.getTimeAnalytics(),
        analyticsService.getBookingsByDateRange(30)
      ]);

      if (metricsResult.success) {
        setOverallMetrics(metricsResult.data);
      }

      if (demographicsResult.success) {
        setDemographics(demographicsResult.data);
      }

      if (appointmentResult.success) {
        setAppointmentAnalytics(appointmentResult.data);
      }

      if (revenueResult.success) {
        setRevenueAnalytics(revenueResult.data);
      }

      if (timeResult.success) {
        setTimeAnalytics(timeResult.data);
      }

      if (dateRangeResult.success) {
        setBookingsByDate(dateRangeResult.data);
      }

      setLastUpdated(new Date());
    } catch (error) {
      setConnectionStatus('failed');
      setError('Failed to load analytics data: ' + error.message);
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  // Export data to CSV
  const handleExport = async () => {
    try {
      const result = await analyticsService.exportToCSV();
      if (result.success) {
        // Create and download CSV file
        const blob = new Blob([result.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = result.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        setError('Failed to export data: ' + (result.error?.message || 'Unknown error'));
      }
    } catch (error) {
      setError('Export failed: ' + error.message);
    }
  };

  // Set up real-time subscription and initial load
  useEffect(() => {
    loadAnalyticsData();

    // Subscribe to real-time changes
    let subscription = null;
    if (connectionStatus === 'connected') {
      subscription = analyticsService.subscribeToAnalytics((payload) => {
        console.log('Real-time analytics update:', payload);
        // Reload data when changes occur
        loadAnalyticsData();
      });
    }

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, []);

  // KPI Cards data
  const kpiCards = [
    {
      id: 'total-bookings',
      title: 'Total Bookings',
      value: overallMetrics.totalBookings || 0,
      icon: FiCalendar,
      color: 'from-blue-500 to-cyan-500',
      change: '+12.5%',
      changeType: 'positive',
      description: 'All time bookings'
    },
    {
      id: 'total-revenue',
      title: 'Total Revenue',
      value: formatCurrency(revenueAnalytics.totalRevenue),
      icon: FiDollarSign,
      color: 'from-green-500 to-emerald-500',
      change: '+8.2%',
      changeType: 'positive',
      description: 'Cumulative earnings'
    },
    {
      id: 'today-bookings',
      title: 'Today\'s Bookings',
      value: overallMetrics.todayBookings || 0,
      icon: FiActivity,
      color: 'from-purple-500 to-pink-500',
      change: '+5.1%',
      changeType: 'positive',
      description: 'Bookings today'
    },
    {
      id: 'confirmation-rate',
      title: 'Confirmation Rate',
      value: formatPercentage(overallMetrics.confirmationRate),
      icon: FiCheckCircle,
      color: 'from-orange-500 to-red-500',
      change: '+2.3%',
      changeType: 'positive',
      description: 'Booking success rate'
    }
  ];

  // Show connection status or loading
  if (connectionStatus === 'checking') {
    return (
      <div className="min-h-[80vh] flex items-center justify-center bg-gray-50">
        <div className="text-center p-8">
          <SafeIcon icon={FiRefreshCw} className="h-16 w-16 animate-spin text-blue-600 mx-auto mb-6" />
          <h3 className="text-xl font-semibold text-gray-900 mb-3">Connecting to Analytics</h3>
          <p className="text-gray-600">Establishing connection to analytics database...</p>
        </div>
      </div>
    );
  }

  if (connectionStatus === 'failed') {
    return (
      <div className="min-h-[80vh] flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md p-8">
          <SafeIcon icon={FiAlertCircle} className="h-20 w-20 text-red-600 mx-auto mb-6" />
          <h3 className="text-2xl font-semibold text-gray-900 mb-4">Analytics Connection Failed</h3>
          <p className="text-gray-600 mb-8">{error}</p>
          <button
            onClick={loadAnalyticsData}
            className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-8xl mx-auto">
        {/* Enhanced Header Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between space-y-4 xl:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="h-16 w-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiTrendingUp} className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-gray-900">Advanced Analytics</h1>
                <p className="text-gray-600 mt-2 text-lg">
                  Real-time insights and comprehensive analytics for Best Podiatrist
                  {lastUpdated && (
                    <span className="block text-sm text-gray-500 mt-1">
                      Last updated: {lastUpdated.toLocaleTimeString()}
                    </span>
                  )}
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-4">
              {/* Connection Status */}
              <div className={`flex items-center px-4 py-2 rounded-full text-sm font-medium ${
                connectionStatus === 'connected' 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-red-100 text-red-700'
              }`}>
                <SafeIcon 
                  icon={connectionStatus === 'connected' ? FiWifi : FiWifiOff} 
                  className="h-4 w-4 mr-2" 
                />
                {connectionStatus === 'connected' ? 'Live Data' : 'Disconnected'}
              </div>

              {/* View Mode Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('detailed')}
                  className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'detailed' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <SafeIcon icon={FiGrid} className="h-4 w-4 mr-2" />
                  Detailed
                </button>
                <button
                  onClick={() => setViewMode('compact')}
                  className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'compact' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <SafeIcon icon={FiList} className="h-4 w-4 mr-2" />
                  Compact
                </button>
              </div>

              {/* Action Buttons */}
              <button
                onClick={handleExport}
                className="flex items-center px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                <SafeIcon icon={FiDownload} className="h-4 w-4 mr-2" />
                Export CSV
              </button>
              
              <motion.button
                onClick={loadAnalyticsData}
                disabled={loading}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg hover:from-blue-700 hover:to-cyan-700 transition-colors font-medium disabled:opacity-50 shadow-sm"
              >
                <SafeIcon 
                  icon={FiRefreshCw} 
                  className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} 
                />
                Refresh Data
              </motion.button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl"
          >
            <div className="flex items-center">
              <SafeIcon icon={FiAlertCircle} className="h-5 w-5 mr-3" />
              <span className="font-medium">{error}</span>
            </div>
          </motion.div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="text-center">
              <SafeIcon icon={FiRefreshCw} className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600 text-lg">Loading analytics data...</p>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Enhanced KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
              {kpiCards.map((card, index) => (
                <motion.div
                  key={card.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white overflow-hidden shadow-sm rounded-xl border border-gray-200 hover:shadow-md transition-all duration-200 cursor-pointer"
                  onClick={() => setExpandedCard(expandedCard === card.id ? null : card.id)}
                >
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className={`h-12 w-12 bg-gradient-to-r ${card.color} rounded-lg flex items-center justify-center`}>
                        <SafeIcon icon={card.icon} className="h-6 w-6 text-white" />
                      </div>
                      <div className={`text-sm font-semibold px-2 py-1 rounded-full ${
                        card.changeType === 'positive' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {card.change}
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <h3 className="text-sm font-medium text-gray-600">{card.title}</h3>
                      <div className="text-3xl font-bold text-gray-900">{card.value}</div>
                      <p className="text-xs text-gray-500">{card.description}</p>
                    </div>

                    {expandedCard === card.id && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="mt-4 pt-4 border-t border-gray-100"
                      >
                        <div className="text-xs text-gray-500">
                          Additional insights and trends for {card.title.toLowerCase()}
                        </div>
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Main Analytics Grid */}
            <div className={`grid gap-6 ${
              viewMode === 'detailed' 
                ? 'grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3' 
                : 'grid-cols-1 lg:grid-cols-2'
            }`}>
              {/* Demographics Section - Enhanced */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className={`bg-white shadow-sm rounded-xl border border-gray-200 ${
                  viewMode === 'detailed' ? 'xl:col-span-1' : ''
                }`}
              >
                <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-cyan-50">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <SafeIcon icon={FiUsers} className="h-5 w-5 mr-3" />
                    Patient Demographics
                  </h3>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Gender Distribution */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-4">Gender Distribution</h4>
                      <div className="space-y-3">
                        {Object.entries(demographics.genderStats || {}).map(([gender, count]) => (
                          <div key={gender} className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-900 capitalize">{gender}</span>
                            <div className="flex items-center space-x-3">
                              <span className="text-sm text-gray-600">{count}</span>
                              <div className="w-20 bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-blue-500 h-2 rounded-full transition-all duration-500" 
                                  style={{ 
                                    width: `${demographics.totalPatients > 0 ? (count / demographics.totalPatients) * 100 : 0}%` 
                                  }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Age Groups */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-4">Age Distribution</h4>
                      <div className="space-y-3">
                        {Object.entries(demographics.ageGroups || {}).map(([ageGroup, count]) => (
                          <div key={ageGroup} className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-900">{ageGroup} years</span>
                            <div className="flex items-center space-x-3">
                              <span className="text-sm text-gray-600">{count}</span>
                              <div className="w-20 bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-green-500 h-2 rounded-full transition-all duration-500" 
                                  style={{ 
                                    width: `${demographics.totalPatients > 0 ? (count / demographics.totalPatients) * 100 : 0}%` 
                                  }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Appointment Types - Enhanced */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-white shadow-sm rounded-xl border border-gray-200"
              >
                <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <SafeIcon icon={FiTarget} className="h-5 w-5 mr-3" />
                    Popular Appointment Types
                  </h3>
                </div>
                <div className="p-6">
                  <div className="space-y-4 max-h-80 overflow-y-auto">
                    {Object.entries(appointmentAnalytics.typeStats || {})
                      .sort(([,a], [,b]) => b - a)
                      .slice(0, 8)
                      .map(([type, count], index) => (
                        <motion.div
                          key={type}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex-1">
                            <span className="text-sm font-medium text-gray-900">{type}</span>
                            <div className="text-xs text-gray-500 mt-1">
                              Revenue: {formatCurrency(appointmentAnalytics.revenueByType?.[type] || 0)}
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="text-lg font-bold text-blue-600">{count}</span>
                            <div className="text-xs text-gray-500">bookings</div>
                          </div>
                        </motion.div>
                      ))}
                  </div>
                </div>
              </motion.div>

              {/* Revenue Analytics - Enhanced */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="bg-white shadow-sm rounded-xl border border-gray-200"
              >
                <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-green-50 to-emerald-50">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <SafeIcon icon={FiDollarSign} className="h-5 w-5 mr-3" />
                    Revenue Insights
                  </h3>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="text-center p-4 bg-green-50 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">
                          {formatCurrency(revenueAnalytics.totalRevenue)}
                        </div>
                        <div className="text-sm text-gray-600">Total Revenue</div>
                      </div>
                      <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">
                          {formatCurrency(revenueAnalytics.averageFee)}
                        </div>
                        <div className="text-sm text-gray-600">Average Fee</div>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                        <span className="text-sm font-medium text-gray-700">Confirmed</span>
                        <span className="text-lg font-bold text-green-600">
                          {overallMetrics.confirmedBookings || 0}
                        </span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg">
                        <span className="text-sm font-medium text-gray-700">Pending</span>
                        <span className="text-lg font-bold text-yellow-600">
                          {overallMetrics.pendingBookings || 0}
                        </span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                        <span className="text-sm font-medium text-gray-700">Cancelled</span>
                        <span className="text-lg font-bold text-red-600">
                          {overallMetrics.cancelledBookings || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Location Analytics - Enhanced */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="bg-white shadow-sm rounded-xl border border-gray-200"
              >
                <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-red-50 to-pink-50">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <SafeIcon icon={FiMapPin} className="h-5 w-5 mr-3" />
                    Patient Locations
                  </h3>
                </div>
                <div className="p-6">
                  <div className="space-y-3 max-h-80 overflow-y-auto">
                    {Object.entries(demographics.locationStats || {})
                      .sort(([,a], [,b]) => b - a)
                      .slice(0, 10)
                      .map(([location, count], index) => (
                        <motion.div
                          key={location}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg transition-colors"
                        >
                          <span className="text-sm font-medium text-gray-900 flex-1">{location}</span>
                          <div className="flex items-center space-x-3">
                            <span className="text-sm text-gray-600 min-w-[2rem] text-right">{count}</span>
                            <div className="w-24 bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-purple-500 h-2 rounded-full transition-all duration-700" 
                                style={{ 
                                  width: `${demographics.totalPatients > 0 ? (count / demographics.totalPatients) * 100 : 0}%` 
                                }}
                              ></div>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                  </div>
                </div>
              </motion.div>

              {/* Accessibility & Time Analytics - Enhanced */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="bg-white shadow-sm rounded-xl border border-gray-200"
              >
                <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <SafeIcon icon={FiUserCheck} className="h-5 w-5 mr-3" />
                    Accessibility & Peak Hours
                  </h3>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Wheelchair Assistance */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-4">Wheelchair Assistance</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                          <span className="text-sm font-medium text-gray-700">Required</span>
                          <span className="text-lg font-bold text-blue-600">
                            {demographics.wheelchairStats?.required || 0}
                          </span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <span className="text-sm font-medium text-gray-700">Not Required</span>
                          <span className="text-lg font-bold text-gray-600">
                            {demographics.wheelchairStats?.notRequired || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Peak Hours */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-4">Peak Hours</h4>
                      <div className="space-y-3">
                        {Object.entries(timeAnalytics.hourStats || {})
                          .sort(([,a], [,b]) => b - a)
                          .slice(0, 4)
                          .map(([hour, count]) => (
                            <div key={hour} className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                              <span className="text-sm font-medium text-gray-700">{hour}:00</span>
                              <span className="text-lg font-bold text-green-600">{count}</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Duration Analytics - Full Width Enhanced */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="bg-white shadow-sm rounded-xl border border-gray-200"
            >
              <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-red-50">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <SafeIcon icon={FiClock} className="h-5 w-5 mr-3" />
                  Appointment Duration Analysis
                </h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4">
                  {Object.entries(appointmentAnalytics.durationStats || {})
                    .sort(([,a], [,b]) => b - a)
                    .map(([duration, count], index) => (
                      <motion.div
                        key={duration}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.1 }}
                        className="text-center p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg hover:from-blue-50 hover:to-cyan-50 transition-all duration-200 border border-gray-200"
                      >
                        <div className="text-3xl font-bold text-gray-900 mb-1">{duration}</div>
                        <div className="text-xs text-gray-500 mb-2">minutes</div>
                        <div className="text-sm font-semibold text-blue-600">{count}</div>
                        <div className="text-xs text-gray-500">bookings</div>
                      </motion.div>
                    ))}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvancedAnalytics;